import { useMutation, useQueryClient } from 'react-query'
import { SuggestionRequestStatus, SuggestionRequestWithRequester } from 'types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useGenerateDesignContent, Options as GenerateOptions, Variables } from './useGenerateDesignContent'
import { fetchSuggestionRequest } from '../aiSuggestionRequests/useAISuggestionRequest'

class AbortedError extends Error {
  constructor() {
    super('The operation was aborted.')
    this.name = 'AbortedError'
  }
}

interface Options extends GenerateOptions {
  /**
   * Callback to be called on each poll interval. Useful for updating the UI
   * with the latest data.
   */
  onPollInterval?: (request: SuggestionRequestWithRequester) => void
}

export const useGenerateDesignContentSync = (options?: Options) => {
  const workspaceId = useCurrentWorkspaceId()
  const { mutateAsync: generateDesignContent } = useGenerateDesignContent({ workspaceId })
  const { onPollInterval, ...restOptions } = options ?? {}
  const queryClient = useQueryClient()

  return useMutation<SuggestionRequestWithRequester, Error, Variables>(
    async (vars) => {
      const { signal, ...variables } = vars

      let request = await generateDesignContent(variables)

      let interval = 500
      do {
        const result = await fetchSuggestionRequest(request.id)

        request = result.request

        onPollInterval?.(request)

        if (signal?.aborted && request) {
          throw new AbortedError()
        }
        if (request.status === SuggestionRequestStatus.Failed) {
          throw new Error('Failed to generate design content')
        }

        await new Promise((resolve) => {
          setTimeout(resolve, interval)
        })
      } while ([SuggestionRequestStatus.Pending, SuggestionRequestStatus.Processing].includes(request.status))

      queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits'])

      return request
    },
    {
      ...restOptions,
      retry: (failureCount, error) => {
        if (error instanceof AbortedError) {
          return false
        }
        if (typeof restOptions.retry === 'function') {
          return restOptions.retry(failureCount, error)
        }
        if (typeof restOptions.retry === 'boolean') {
          return restOptions.retry
        }
        return failureCount < (restOptions.retry ?? 0)
      },
    },
  )
}
